import React from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ListingTableFooter from "./ListingTableFooter";
import { ListingTableCell } from "./styles";
import { useVisibleTableColumns } from "../../../hooks/useVisibleTableColumns";

// ===== Single source of truth for spacing =====
const BASE_X_PADDING = 3; // spacing between cells
const EDGE_X_PADDING = 3; // outer gutter for first/last column
const ROW_Y_PADDING = 0.5;  // vertical padding (row height feel)

function mergeSx(a, b) {
  if (!a && !b) return undefined;
  if (!a) return b;
  if (!b) return a;

  const arrA = Array.isArray(a) ? a : [a];
  const arrB = Array.isArray(b) ? b : [b];

  return [...arrA, ...arrB];
}

function cellSpacingSx({ isFirst, isLast }) {
  return {
    paddingTop: ROW_Y_PADDING,
    paddingBottom: ROW_Y_PADDING,
    paddingLeft: isFirst ? EDGE_X_PADDING : BASE_X_PADDING,
    paddingRight: isLast ? EDGE_X_PADDING : BASE_X_PADDING,
  };
}

// Forces inner flex containers (e.g., StyledStack) to respect column alignment
function innerContentAlignSx(align) {
  const justify =
    align === "right" ? "flex-end" : align === "center" ? "center" : "flex-start";

  return {
    "& .MuiStack-root": {
      justifyContent: justify,
      width: "auto",
      maxWidth: "100%",
    },
    "& .MuiStack-root > *": {
      maxWidth: "100%",
    },
  };
}

export default function ListingDataTableForModal({
  columns,
  rows,
  loading = false,
  emptyText = "No records found.",
  rowKey = (row, idx) => row?.id ?? row?.cyreen_id ?? idx,

  pagination,
  onPageChange,
  onPageSizeChange,

  tableMinWidth = 700,
  footerRightActions = null,
  tableLayout = "auto", // set to "fixed" if you want consistent widths
  fitContent = false,
}) {
  const visibleColumns = useVisibleTableColumns(columns, rows);

  const lastIdx = visibleColumns.length - 1;

  const getHeaderAlign = (col) => col.headerAlign ?? col.align ?? "left";
  const getCellAlign = (col) => col.cellAlign ?? col.align ?? "left";

  return (
    <Box>
      <TableContainer component={Paper}>
        <Table
          sx={
            fitContent
              ? // fill the available width, but never shrink below content width (no wrapping; scrolls instead)
                { tableLayout, width: "100%", minWidth: "max-content" }
              : { minWidth: tableMinWidth, tableLayout }
          }
          aria-label="listing-table"
        >
          <TableHead>
            <TableRow>
              {visibleColumns.map((col, idx) => {
                const isFirst = idx === 0;
                const isLast = idx === lastIdx;
                const align = getHeaderAlign(col);

                return (
                  <ListingTableCell
                    key={col.id}
                    align={align}
                    // className={col.headerClassName ?? "text-wrapper"}
                    onClick={col.onHeaderClick}
                    colSpan={col?.applyColSpan === true && 2}
                    sx={mergeSx(
                      cellSpacingSx({ isFirst, isLast }),
                      mergeSx(innerContentAlignSx(align), col.headerSx)
                    )}
                    style={col.onHeaderClick ? { cursor: "pointer" } : undefined}
                  >
                    {col.header}
                  </ListingTableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <ListingTableCell
                  colSpan={visibleColumns.length}
                  align="left"
                  sx={cellSpacingSx({ isFirst: true, isLast: true })}
                >
                  <Typography variant="body2">Loading…</Typography>
                </ListingTableCell>
              </TableRow>
            ) : rows?.length ? (
              rows.map((row, rowIdx) => (
                <TableRow key={rowKey(row, rowIdx)}>
                  {visibleColumns.map((col, colIdx) => {
                    const isFirst = colIdx === 0;
                    const isLast = colIdx === lastIdx;
                    const align = getCellAlign(col);

                    return (
                      <ListingTableCell
                        key={col.id}
                        align={align}
                        colSpan={col?.applyColSpan === true && 2}
                        // className={col.cellClassName ?? "text-wrapper"}
                        sx={mergeSx(
                          cellSpacingSx({ isFirst, isLast }),
                          mergeSx(innerContentAlignSx(align), col.cellSx)
                        )}
                      >
                        {col.render ? col.render(row, rowIdx) : row?.[col.accessor]}
                      </ListingTableCell>
                    );
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <ListingTableCell
                  colSpan={visibleColumns.length}
                  align="left"
                  sx={cellSpacingSx({ isFirst: true, isLast: true })}
                >
                  <Typography variant="body2">{emptyText}</Typography>
                </ListingTableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <ListingTableFooter
        pagination={pagination}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        rightActions={footerRightActions}
      />
    </Box>
  );
}
